import {DescriptionRounded, FileDownloadRounded, LibraryAddRounded, SendRounded,} from "@mui/icons-material";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import AdvancedFilter from "../../component/AdvancedFilter";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import * as Yup from 'yup';
import {Box, Button, Checkbox, Chip, Grid, IconButton, ListItemText, MenuItem, TextField} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useNavigate} from "react-router-dom";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridOptions, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {EventStatusInterface, EventTypeInterface} from "../../component/TheftMisappropriationInterfaces";
import {useEventTypeValues} from "../../component/useEventTypeValues";
import {useStatusValues} from "../../component/useStatusValues";
import EmptyState from "../../../../util/component/EmptyState";
import dayjs from "dayjs";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useTheftMisappropriationActions} from "../../service/useTheftMisappropriationActions";
import {CREATED, IN_PROGRESS, NOT_FOUND, RECOVERED, STATE_SUMMARY_UPDATING} from "../../component/Constants";
import {NumericSummary} from "../../component/NumericSummary";
import {useSetRecoilState} from "recoil";
import CustomModalBL from "../../component/CustomModalBL";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import NotAuthorizedDialog from "../../../authentication/component/NotAuthorizedDialog";
import {
    SCOPE_THEFT_MISAPPROPRIATION,
    THEFT_MISAPPROPRIATION_SCOPE_CREATE,
    THEFT_MISAPPROPRIATION_SCOPE_DETAIL,
    THEFT_MISAPPROPRIATION_SCOPE_EXPORT_TO_BE_CHARGED,
    THEFT_MISAPPROPRIATION_SCOPE_SEND_IN_INVOICING
} from "../../component/utils/AuthConstants";
import {renderMultiSelect} from "../../common/FormSelect";

const TheftMisappropriationList = () => {
    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'theftMisappropriate'});
    const {t: tr} = useTranslation("translation");
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const {
        getTheftMisappropriationList,
        getEventType,
        getEventStatus,
        downloadReport,
        getSummary,
        sendToInvoice
    } = useTheftMisappropriationActions();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [theftMisappropriationTableShouldUpdate, setTheftMisappropriationTableShouldUpdate] = useState(false);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const [eventType, setEventType] = useState<EventTypeInterface[]>([]);
    const [eventStatus, setEventStatus] = useState<EventStatusInterface[]>([]);
    const [emptyList, setEmptyList] = useState<any>(null);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const [openSendInInvoicing, setOpenSendInInvoicing] = useState(false);
    const {getEventTypeValues} = useEventTypeValues();
    const {getStatusValues, getStatusColor} = useStatusValues();
    const legalEntityOptions: any[] = globalData?.user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = globalData?.user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`dealers.${x.name}`),
        }));
    const [dealers, setDealers] = React.useState<any[]>(dealerOptions);

    const filterDefaultValues = {
        firstName: "", lastName: "", rentalAgreement: "", entryUser: "", eventType: "", eventStatus: [],
        openingDateFrom: null, openingDateTo: null, closingDateFrom: null, closingDateTo: null,
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };
    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [doSearchFlag, setDoSearchFlag] = useState<boolean>(false);
    const [legalEntityModal, setLegalEntityModal] = useState<any>("");
    const [dealerModal, setDealerModal] = useState<any>("");


    const validationObj = {
        eventStatus: Yup.array(),
        openingDateFrom: Yup.date().nullable()
            .test("testOpeningDateFrom", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.openingDateTo || value <= context.parent.openingDateTo)
            })
            .typeError(t('invalidDate')),
        openingDateTo: Yup.date().nullable()
            .test("testOpeningDateTo", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.openingDateFrom || value >= context.parent.openingDateFrom)
            })
            .typeError(t('invalidDate')),
        closingDateFrom: Yup.date().nullable()
            .test("testClosingtDateFrom", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.closingDateTo || value <= context.parent.closingDateTo)
            })
            .typeError(t('invalidDate')),
        closingDateTo: Yup.date().nullable()
            .test("testClosingDateTo", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.closingDateFrom || value >= context.parent.closingDateFrom)
            })
            .typeError(t('invalidDate')),
        customer: Yup.string().nullable(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tr('validation.selectAtLeastOne'))
    }

    useEffect(() => {
        getEventType().then((res: any) => {
            setEventType(res.result);
        });
        getEventStatus().then((res: any) => {
            setEventStatus(res.result);
        });

    }, []);

    const renderFirstName = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.firstName')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderLastName = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.lastName')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderIdRentalAgreement = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.idRentalAgreement')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderEntryUser = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.entryUser')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderEventType = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.eventType')}
            select
            value={field.value}
            onChange={field.onChange}
        >
            <MenuItem key={""} value={""}/>
            {eventType?.map((l) => (
                <MenuItem key={l.eventTypeCode} value={l.eventTypeCode}>
                    {getEventTypeValues(l.eventTypeCode)}
                </MenuItem>
            ))}

        </TextField>
    )
    const renderStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.status')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => {
                    return eventStatus.length > 0 ? selected.map((l: any) =>
                        getStatusValues(l)).join(", ") : "";
                },
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {eventStatus?.map((l) => (
                <MenuItem key={l.eventStatusCode} value={l.eventStatusCode}>
                    <Checkbox color={"accent"} checked={field.value.indexOf(l.eventStatusCode) > -1}/>
                    <ListItemText primary={getStatusValues(l.eventStatusCode)}/>
                </MenuItem>
            ))}
        </TextField>
    )
    const renderOpeningDateFrom = (field: any, error: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.openingDateFrom')}
            value={field.value}
            onChange={field.onChange}
            maxDate={values["openingDateTo"] ? values["openingDateTo"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"openingDateFrom"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderOpeningDateTo = (field: any, error: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.openingDateTo')}
            value={field.value}
            onChange={field.onChange}
            minDate={values["openingDateFrom"] ? values["openingDateFrom"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"openingDateTo"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderClosingDateFrom = (field: any, error: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.closingDateFrom')}
            value={field.value}
            onChange={field.onChange}
            maxDate={values["closingDateTo"] ? values["closingDateTo"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"closingDateFrom"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderClosingDateTo = (field: any, error: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.closingDateTo')}
            value={field.value}
            onChange={field.onChange}
            minDate={values["closingDateFrom"] ? values["closingDateFrom"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"closingDateTo"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)

    const renderPlate = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.plate')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const mainFields = {
        firstName: renderFirstName,
        lastName: renderLastName,
        rentalAgreement: renderIdRentalAgreement,
        plate: renderPlate
    }
    const fields = {
        eventType: renderEventType,
        eventStatus: renderStatus,
        openingDateFrom: renderOpeningDateFrom,
        openingDateTo: renderOpeningDateTo,
        closingDateFrom: renderClosingDateFrom,
        closingDateTo: renderClosingDateTo,
        entryUser: renderEntryUser,
        legalEntities: renderMultiSelect(
            tr("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = globalData?.user?.dealers
                    ?.filter(
                        (x: any) =>
                            value?.length > 0 &&
                            x.legalEntities.some((e: any) => value.includes(e.name)) &&
                            x.name.toLowerCase() !== "all"
                    )
                    .map((x: any) => {
                        return {
                            label: tr(`dealers.${x.name}`),
                            value: x.name,
                        };
                    });
                setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tr("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),

    }

    const onDownloadTemplate = () => {
        downloadReport(filters.legalEntities, filters.dealers)
            .then((response: any) => {
                if (response.status === "OK") {
                    const href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.result.file}`;
                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', 'theftAndMisappropriation_' + dayjs().format("L") + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                } else {
                    let message = (response?.message ?
                        t(`handleErrors.${response.message}`) :
                        t(`clientCallFailMessage`));
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: message,
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("handleErrors.downloadError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        })
    }

    const sendReportsToInvoice = () => {
        let legalEntity = legalEntityModal;
        let dealer = dealerModal;
        if (legalEntityOptions.length <= 1) {
            legalEntity = legalEntityOptions[0].value;
            dealer = dealerOptions[0].value;
        }
        sendToInvoice(legalEntity, dealer).then(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("updateForm.sendInvoiceSuccess"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("updateForm.sendInvoiceKO"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        })
    }

    const toolbar = {
        renderInfo: () => (
            <NumericSummary
                query={() => getSummary({...filters})}
                filters={filters}
                fields={[CREATED, IN_PROGRESS, NOT_FOUND, RECOVERED]

                } label={"eventInfo"}/>
        ),
        renderActions: () =>
            <>
                {hasScope(THEFT_MISAPPROPRIATION_SCOPE_EXPORT_TO_BE_CHARGED) &&
                    <Button
                        disabled={emptyList === 0}
                        variant={"outlined"} size="small"
                        onClick={() => onDownloadTemplate()}> <FileDownloadRounded/>
                        {t('buttons.exportCard')}
                    </Button>}
                {hasScope(THEFT_MISAPPROPRIATION_SCOPE_SEND_IN_INVOICING) &&
                    <Button size="small" variant={"outlined"}
                            disabled={emptyList === 0}
                            onClick={() => {
                                setOpenSendInInvoicing(true);
                            }}
                            startIcon={<SendRounded/>}
                    >
                        {t('buttons.sendToInvoice')}
                    </Button>}
                {hasScope(THEFT_MISAPPROPRIATION_SCOPE_CREATE) &&
                    <Button size="small" startIcon={<LibraryAddRounded/>}
                            onClick={() => {
                                navigate('/finessecurity/theft-misappropriation/addEventCard');
                            }}>
                        {t('buttons.createCard')}
                    </Button>}
            </>
    }

    const columns: ColDef[] = [
        {
            field: "firstName",
            headerName: t('listColumns.firstName'),
            unSortIcon: true,
            valueGetter: (params: any) => (params.data.firstName ? params.data.firstName : ""),
            tooltipValueGetter: (params: any) => {
                return params.data.firstName
            },
        },
        {
            field: "lastName",
            headerName: t('listColumns.lastName'),
            unSortIcon: true,
            valueGetter: (params: any) => (params.data.lastName ? params.data.lastName : ""),
            tooltipValueGetter: (params: any) => {
                return params.data.lastName
            },
        },
        {
            field: "rentalAgreement",
            headerName: t('listColumns.idRentalAgreement'),
            unSortIcon: true,
            tooltipField: "rentalAgreement",
        },
        {
            field: "eventType",
            headerName: t('listColumns.eventType'),
            sortable: false,
            sort: undefined,
            cellRenderer: (params: any) => getEventTypeValues(params.value.eventTypeCode),
            tooltipValueGetter: (params: any) => getEventTypeValues(params.value.eventTypeCode),
        },
        {
            field: "openingDatetime",
            headerName: t('listColumns.openingDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "closingDatetime",
            headerName: t('listColumns.closingDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "entryUser",
            headerName: t('listColumns.entryUser'),
            unSortIcon: true
        },
        {
            field: "plate",
            headerName: t('listColumns.plate'),
            unSortIcon: true
        },
        {
            field: "eventStatus",
            headerName: t('filters.status'),
            cellRenderer: (params: any) => {
                return <Chip label={getStatusValues(params.value.eventStatusCode)}
                             sx={{backgroundColor: getStatusColor(params.value.eventStatusCode)}}/>
            }
        },
        {
            field: "tenant",
            headerName: t('listColumns.tenant'),
            unSortIcon: true
        },
        {
            field: "dealer",
            headerName: t('listColumns.dealer'),
            unSortIcon: true
        },
        {
            field: "_actions",
            headerName: t('listColumns.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            hide: !hasScope(THEFT_MISAPPROPRIATION_SCOPE_DETAIL),
            width: 100,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <IconButton color={"accent"}
                                    onClick={() => {
                                        window.open(`/finessecurity/theft-misappropriation/detail/${params.data.id}`, "_blank");
                                        //navigate(`/finessecurity/theft-misappropriation/detail/${params.data.id}`)
                                    }}>
                            <DescriptionRounded/>
                        </IconButton>
                    </Box>
                )
            }
        }
    ];

    function createDatasource(filters: any) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                if (!filters.legalEntities) {
                    params.success({rowCount: 0, rowData: []});
                    params.api.hideOverlay();
                    return;
                }

                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                try {
                    let data = await getTheftMisappropriationList(filterTemp);

                    if (data.totalElements === 0) {
                        gridRef.current!.api.showNoRowsOverlay();
                    } else {
                        gridRef.current!.api.hideOverlay();
                    }
                    setEmptyList(data.totalElements)
                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        serverSideDatasource: createDatasource(filters)
    };
    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);
    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t("noEvents"),
            content: t("noEventsDescription")
        };
    }, []);

    const retrieveList = () => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters));
        setDoSearchFlag(false);
        setSummaryUpdating(true);
    };

    useEffect(() => {
        if (doSearchFlag) {
            retrieveList();
        }
    }, [doSearchFlag]);

    const handleSetFilters = (data: any) => {
        setFilters(data);
        setDoSearchFlag(true);
    }

    useEffect(() => {
        if (theftMisappropriationTableShouldUpdate) {
            gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters));
            setTheftMisappropriationTableShouldUpdate(false);
        }
    }, [theftMisappropriationTableShouldUpdate]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const reset = () => {
        setFilters([]);
    };

    const handleOnSave = () => {
        setOpenSendInInvoicing(false);
        sendReportsToInvoice();
    }

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        setLegalEntityModal(value);
        let dealers = globalData?.user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tr(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }


    return (
        <>
            {hasScope(SCOPE_THEFT_MISAPPROPRIATION) ?
                <>
                    <Box>
                        <SectionHeader title={t('listHeader.title')}
                                       subtitle={t('listHeader.subTitle')}/>
                        <AdvancedFilter validationObj={validationObj} fields={fields}
                                        defaultValues={filterDefaultValues} onReset={reset}
                                        mainFields={mainFields} filters={filters} setFilters={handleSetFilters}/>

                        <Grid container>
                            <Grid item xs={12} sx={{padding: "16px"}}>
                                <AgGridCustomTable gridOptions={gridOptions}
                                                   gridRef={gridRef}
                                                   toolbar={toolbar}
                                                   noRowsOverlayComponent={noRowsOverlayComponent}
                                                   noRowsOverlayComponentParams={noRowsOverlayComponentParams}/>
                            </Grid>
                        </Grid>
                    </Box>
                    <CustomModalBL open={openSendInInvoicing} title={t('updateForm.sendConfirmation')}
                                   content={
                                       <>
                                           {t('updateForm.sendConfirmationMsg')}
                                           {legalEntityOptions.length > 1 &&
                                               <>
                                                   <Grid container spacing={2} alignItems={'flex-end'}>
                                                       <Grid item md={6} sm={3} xs={12}>
                                                           <TextField
                                                               name={"legalEntity"}
                                                               fullWidth
                                                               select
                                                               label={tr("shared.legalEntity")}
                                                               value={legalEntityModal}
                                                               onChange={(event) => handleChangeLegalEntities(event.target.value)}
                                                           >
                                                               {legalEntityOptions.map((option: any) => (
                                                                   <MenuItem key={option.value} value={option.value}>
                                                                       {option.label}
                                                                   </MenuItem>
                                                               ))}
                                                           </TextField>
                                                       </Grid>

                                                       <Grid item md={6} sm={3} xs={12}>
                                                           <TextField
                                                               name={"dealer"}
                                                               fullWidth
                                                               select
                                                               label={tr("shared.dealer")}
                                                               value={dealerModal}
                                                               onChange={(event) => setDealerModal(event.target.value)}
                                                           >
                                                               {dealerOptions.map((option: any) => (
                                                                   <MenuItem key={option.value} value={option.value}>
                                                                       {option.label}
                                                                   </MenuItem>
                                                               ))}
                                                           </TextField>
                                                       </Grid>
                                                   </Grid>
                                               </>
                                           }

                                       </>

                                   }
                                   cancelButton={t('buttons.cancel')}
                                   saveButton={t('buttons.confirmSending')}
                                   isDisabled={legalEntityOptions.length > 1 && (legalEntityModal === "" || dealerModal === "")}
                                   titleIcon={<InfoRoundedIcon/>}
                                   onSave={handleOnSave}
                                   onClose={() => {
                                       setOpenSendInInvoicing(false);
                                       setLegalEntityModal(null);
                                       setDealerModal(null);
                                   }}
                    /></>
                :
                <NotAuthorizedDialog/>}
        </>
    )
}

export default TheftMisappropriationList;
